<template>
  <div>
    <b-table
        striped
        hover
        responsive
        :items="visibleInvoices"
        :fields="fields"
        class="building-invoice-tbl"
        show-empty
    >
      <!-- Status badge -->
      <template #cell(invoiceStatus)="{ item }">
        <b-badge :variant="statusVariant(item.invoiceStatus)" class="text-uppercase px-75 py-50">
          {{ item.invoiceStatus }}
        </b-badge>
      </template>

      <!-- Amount with currency -->
      <template #cell(totalAmount)="{ item }">
        <span class="font-weight-bold">{{ formatAmount(item.totalAmount, item.currency) }}</span>
      </template>

      <!-- Creation date -->
      <template #cell(creationDate)="{ item }">
        <span class="text-nowrap text-muted small">{{ formatDate(item.creationDate) }}</span>
      </template>

      <!-- Actions: view-only links -->
      <template #cell(actions)="{ item }">
        <div class="d-flex align-items-center" style="gap: 6px;">

          <!-- Download PDF -->
          <b-button
              v-if="item.links && item.links.pdfLink"
              v-b-tooltip.hover :title="msg('Download PDF')"
              variant="outline-secondary"
              size="sm"
              class="btn-icon px-50"
              @click.stop="openLink(item.links.pdfLink)"
          >
            <feather-icon icon="DownloadIcon" size="13"/>
          </b-button>

          <!-- Open payment link (OPEN invoices only) -->
          <b-button
              v-if="item.invoiceStatus && item.invoiceStatus.toUpperCase() === 'OPEN' && item.links && item.links.paymentLink"
              v-b-tooltip.hover :title="msg('Pay invoice')"
              variant="outline-info"
              size="sm"
              class="btn-icon px-50"
              @click.stop="openLink(item.links.paymentLink)"
          >
            <feather-icon icon="CreditCardIcon" size="13"/>
          </b-button>

        </div>
      </template>

      <template #empty>
        <div class="text-center text-muted py-3">
          <feather-icon icon="FileTextIcon" size="32" class="mb-1"/>
          <p class="mb-0 small">{{ msg('No invoices yet') }}</p>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "BuildingInvoiceTable",
  props: {
    value: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters('translator', ['msg']),

    fields() {
      return [
        { key: 'title', label: this.msg('Title') },
        { key: 'invoiceStatus', label: this.msg('Status'), thStyle: { width: '110px' } },
        { key: 'totalAmount', label: this.msg('Amount'), thStyle: { width: '120px' } },
        { key: 'creationDate', label: this.msg('Created'), thStyle: { width: '120px' } },
        { key: 'actions', label: '', thStyle: { width: '90px' } },
      ];
    },

    // Only show invoices the user needs to act on or has a record of
    visibleInvoices() {
      const all = (this.value && this.value.buildingInvoices) ? this.value.buildingInvoices : [];
      return all.filter(inv => {
        const status = (inv.invoiceStatus || '').toUpperCase();
        return status === 'OPEN' || status === 'PAID';
      });
    },
  },
  methods: {
    statusVariant(status) {
      return { OPEN: 'warning', PAID: 'success' }[(status || '').toUpperCase()] || 'secondary';
    },

    formatAmount(amount, currency) {
      if (amount == null) return '—';
      const sym = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
      return `${sym}${parseFloat(amount).toFixed(2)}`;
    },

    formatDate(dateVal) {
      if (!dateVal) return '—';
      try {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch { return '—'; }
    },

    openLink(url) {
      if (url) window.open(url, '_blank');
    },
  },
}
</script>

<style scoped>
.building-invoice-tbl thead th {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}
.btn-icon { line-height: 1; }
</style>
